/**
 * 四门户文件 API 公共件（FE-E2b 下沉）：共享类型 + mock blob 工具 +
 * Content-Disposition filename 解析 + multipart 上传模板。
 *
 * 端差异（fetchContent / delete 的通道形态：admin=client.download、agent=client.getBlob、
 * merchant/customer=裸 fetch credentials:omit）超参数化能力，保留各端本地实现——
 * 见 FE-E2b 报告迁/keep 清单。本模块不 import 任何门户代码（verify-p2-boundary）。
 */

// ===================== 共享类型 =====================

export type FileUploadSlot = 'IDENTITY' | 'FUND';

export type FileScanStatus = 'CLEAN' | 'PENDING' | 'REJECTED';

export type FileUploadResult = {
  fileId: string;
  contentType: string;
  sizeBytes: number;
  originalFilename?: string | null;
  scanStatus?: FileScanStatus | string | null;
};

export type FileContentResult =
  | { ok: true; blob: Blob; fileName?: string }
  | { ok: false; error: string; status?: number; code?: string };

// ===================== mock blob 工具（四端逐字一致） =====================

const MOCK_PNG_B64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

export function mockImageBlob(): Blob {
  const bin = atob(MOCK_PNG_B64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'image/png' });
}

export function mockPdfBlob(): Blob {
  return new Blob(['%PDF-1.1\ntrailer<</Root 1 0 R>>\n%%EOF\n'], { type: 'application/pdf' });
}

export function looksLikePdf(key: string): boolean {
  return key.toLowerCase().includes('.pdf');
}

// ===================== Content-Disposition filename 解析（merchant/customer 原文） =====================

export function parseContentDispositionFileName(header: string | null): string | undefined {
  if (!header) {
    return undefined;
  }
  const utf8 = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    const value = utf8[1].trim().replace(/^["']|["']$/g, '');
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  const plain = /filename=([^;]+)/i.exec(header);
  if (plain?.[1]) {
    return plain[1].trim().replace(/^["']|["']$/g, '');
  }
  return undefined;
}

// ===================== multipart 上传模板 =====================

/** 上传结果最小面（与四端 ApiResult 结构兼容） */
type FileUploadApiResult =
  | { ok: true; data: FileUploadResult }
  | { ok: false; error: string; code?: string; traceId?: string; status?: number };

/**
 * 构建 `file` + `slot`（+ 可选 directory）的 FormData 并走端 client 上传。
 * 契约：multipart 字段名仅 file + slot；directory 可选。
 */
export function uploadFile(
  upload: (path: string, form: FormData, mock: () => FileUploadResult) => Promise<FileUploadApiResult>,
  file: File,
  slot: FileUploadSlot,
  directory: string,
): Promise<FileUploadApiResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('slot', slot);
  if (directory) {
    form.append('directory', directory);
  }
  return upload('/files', form, () => ({
    fileId: `${directory}/mock-${Date.now()}-${file.name}`,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    originalFilename: file.name,
    scanStatus: 'CLEAN',
  }));
}
